import styled from '@emotion/styled';
import { AutoAwesome } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { isAxiosError } from 'axios';
import { useState } from 'react';

import { ModalNotification } from 'components/common/Modal';
import EmailRequiredToPromoteModal from './EmailRequiredToPromoteModal';

import api from 'api';
import useTranslate from 'hooks/useTranslate';

const Button = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    background: var(--profile-primary, #6025d8);
    color: var(--profile-on-primary, #ffffff);
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.01em;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--profile-primary-dark, #4c1dab);
    }

    &:focus-visible {
        outline: 2px solid var(--profile-primary, #6025d8);
        outline-offset: 2px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
`;

type PromoteState = 'idle' | 'promoting' | 'error';

type ApiErrorResponse = {
    msg?: string;
};

const EMAIL_REQUIRED_TO_PROMOTE_MESSAGE = 'An email is required before promoting to an account';

const isEmailRequiredToPromoteError = (error: unknown) =>
    isAxiosError<ApiErrorResponse>(error) &&
    error.response?.status === 422 &&
    error.response.data?.msg === EMAIL_REQUIRED_TO_PROMOTE_MESSAGE;

interface ActivateAccountButtonProps {
    /** The PROFILE_ONLY user to promote to ACCOUNT. */
    userId: number;
    /** Called after a successful promotion so the parent can refresh and notify. */
    onPromoted?: () => void | Promise<void>;
}

const ActivateAccountButton = ({ userId, onPromoted }: ActivateAccountButtonProps) => {
    const { translate } = useTranslate();
    const [state, setState] = useState<PromoteState>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isEmailRequiredModalOpen, setIsEmailRequiredModalOpen] = useState(false);

    const promoteAccount = async () => {
        if (state === 'promoting') {
            return;
        }

        setState('promoting');
        setErrorMessage(null);
        try {
            await api.clients.promoteToAccount(userId);
            await onPromoted?.();
            setState('idle');
            setIsEmailRequiredModalOpen(false);
        } catch (error) {
            if (isEmailRequiredToPromoteError(error)) {
                setErrorMessage(null);
                setState('idle');
                setIsEmailRequiredModalOpen(true);
                return;
            }

            console.error(error);
            setState('error');
            setErrorMessage(translate('activateAccountError'));
        }
    };

    return (
        <Wrapper>
            <Tooltip
                title={translate('activateAccountTooltip')}
                placement="bottom"
                disableInteractive
            >
                <Button type="button" disabled={state === 'promoting'} onClick={promoteAccount}>
                    {state === 'promoting' ? (
                        <CircularProgress
                            size={14}
                            sx={{ color: 'var(--profile-on-primary, #ffffff)' }}
                        />
                    ) : (
                        <AutoAwesome sx={{ fontSize: 15 }} />
                    )}
                    {translate('activateAccount')}
                </Button>
            </Tooltip>
            {errorMessage && (
                <ModalNotification
                    variant="error"
                    title={translate('errorTitle')}
                    message={errorMessage}
                />
            )}
            <EmailRequiredToPromoteModal
                open={isEmailRequiredModalOpen}
                userId={userId}
                onClose={() => setIsEmailRequiredModalOpen(false)}
                onEmailSaved={promoteAccount}
            />
        </Wrapper>
    );
};

export default ActivateAccountButton;
